import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { Box, Checkbox, FormControlLabel, IconButton, MenuItem, TextField } from '@mui/material';

import { PillRadioGroup } from '~/components/common/PillRadioGroup';
import type { KnowledgeEntry, KnowledgeEntryKindEnum } from '~/data/generated/model';
import { propertiesKnowledgeCreateBodyContentMax } from '~/data/generated/zod/properties/properties.zod';
import { TRANSIENT_SURFACE_RADIUS } from '~/theme';

import {
  formatKnowledgeDate,
  KNOWLEDGE_KIND_LABELS,
  KNOWLEDGE_KIND_OPTIONS,
} from './KnowledgeBits';
import { findSupersedable } from './knowledgeMatch';

export interface KnowledgeDraft {
  key: string;
  kind: KnowledgeEntryKindEnum;
  carId: string | null;
  content: string;
  followUp: string;
  supersede: boolean;
}

let draftSeq = 0;

export function newKnowledgeDraft(
  fields: Partial<Omit<KnowledgeDraft, 'key'>> = {},
): KnowledgeDraft {
  draftSeq += 1;
  return {
    key: `draft-${draftSeq}`,
    kind: 'note',
    carId: null,
    content: '',
    followUp: '',
    supersede: false,
    ...fields,
  };
}

export function supersedableFor(
  existing: KnowledgeEntry[],
  propertyId: string,
  draft: KnowledgeDraft,
): KnowledgeEntry | undefined {
  return findSupersedable(existing, { propertyId, carId: draft.carId, content: draft.content });
}

type Props = {
  draft: KnowledgeDraft;
  propertyId: string;
  cars: readonly { id: string; name: string }[];
  existing: KnowledgeEntry[];
  rows?: number;
  allowFollowUp: boolean;
  onChange: (patch: Partial<KnowledgeDraft>) => void;
  onRemove?: () => void;
};

export function KnowledgeDraftRow({
  draft,
  propertyId,
  cars,
  existing,
  rows = 2,
  allowFollowUp,
  onChange,
  onRemove,
}: Props) {
  const older = supersedableFor(existing, propertyId, draft);

  return (
    <Box
      data-testid="knowledge-draft"
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${TRANSIENT_SURFACE_RADIUS}px`,
        p: '10px 8px 10px 12px',
        bgcolor: 'background.paper',
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-start',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          <PillRadioGroup
            label="Kind"
            showLabel={false}
            options={KNOWLEDGE_KIND_OPTIONS}
            value={draft.kind}
            onChange={(kind) => onChange({ kind })}
          />
          <TextField
            select
            size="small"
            label="Elevator"
            value={draft.carId ?? ''}
            onChange={(e) => onChange({ carId: e.target.value || null })}
            slotProps={{
              inputLabel: { shrink: true },
              select: { displayEmpty: true },
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Whole property</MenuItem>
            {cars.map((car) => (
              <MenuItem key={car.id} value={car.id}>
                {car.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <TextField
          multiline
          fullWidth
          size="small"
          minRows={rows}
          placeholder="What should the team know?"
          value={draft.content}
          onChange={(e) => onChange({ content: e.target.value })}
          slotProps={{
            htmlInput: {
              'aria-label': 'What should the team know?',
              maxLength: propertiesKnowledgeCreateBodyContentMax,
            },
          }}
        />
        {allowFollowUp && draft.kind === 'commitment' ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '8px',
              fontSize: 12,
              color: 'text.secondary',
            }}
          >
            <EventOutlinedIcon sx={{ fontSize: 16, color: '#8a5a00' }} />
            Follow-up
            <TextField
              type="date"
              size="small"
              value={draft.followUp}
              onChange={(e) => onChange({ followUp: e.target.value })}
              slotProps={{ htmlInput: { 'aria-label': 'Follow-up date' } }}
            />
            <span>
              {draft.followUp
                ? 'Also creates a follow-up task'
                : 'Optional · add a date to also create a follow-up task'}
            </span>
          </Box>
        ) : null}
        {older ? (
          <Box
            data-testid="supersede-suggestion"
            sx={{
              p: '4px 10px 6px 4px',
              borderRadius: `${TRANSIENT_SURFACE_RADIUS}px`,
              bgcolor: '#fff7e8',
              border: '1px solid #f0cf8f',
            }}
          >
            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0, gap: '2px' }}
              control={
                <Checkbox
                  size="small"
                  checked={draft.supersede}
                  onChange={(e) => onChange({ supersede: e.target.checked })}
                  sx={{ color: '#8a5a00', '&.Mui-checked': { color: '#8a5a00' }, p: '4px' }}
                />
              }
              label={
                <Box sx={{ fontSize: 12, pt: '5px' }}>
                  <Box sx={{ fontWeight: 500, color: '#5c3d00' }}>
                    Mark the older entry as superseded
                  </Box>
                  <Box sx={{ color: '#5c4a2a', mt: '2px', lineHeight: 1.4 }}>
                    “{older.content}” · {KNOWLEDGE_KIND_LABELS[older.kind]} ·{' '}
                    {formatKnowledgeDate(older.createdAt)}
                  </Box>
                </Box>
              }
            />
          </Box>
        ) : null}
      </Box>
      {onRemove ? (
        <IconButton
          size="small"
          title="Delete"
          aria-label="Delete draft"
          onClick={onRemove}
          sx={{ color: 'error.main' }}
        >
          <DeleteOutlinedIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
}
